import React, { useEffect, useRef, useState } from "react";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import { Close } from "@dreamhub/designsystem/icons";
import SettingItemLayout, {
  SettingItemLayoutProps
} from "@/pages/settings/components/settingsItems/SettingItemLayout";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { useTranslation } from "react-i18next";
import {
  FOCUS_AFTER_OPEN_DELAY_MS,
  focusOrgContextField,
  ORG_CONTEXT_FIELDS,
  OrgContextValues
} from "@/pages/settings/components/orgContext";

const TRANSLATION_KEY = "settings.general.orgContext";

interface OrgContextDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  values: OrgContextValues;
  onSaved: (values: OrgContextValues) => Promise<void>;
  // When opened from an empty-state CTA, focus/scroll to this field's editor so the admin
  // lands directly on the section they clicked. Undefined (e.g. the "Edit" button) → no focus.
  focusField?: keyof OrgContextValues;
}

const OrgContextDrawer: React.FC<OrgContextDrawerProps> = ({
  isOpen,
  onClose,
  values,
  onSaved,
  focusField
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<OrgContextValues>(values);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fieldRefs = useRef<
    Partial<Record<keyof OrgContextValues, HTMLDivElement | null>>
  >({});

  // Seed the whole draft on the closed → open transition. While open, the parent keeps
  // refetching (e.g. its description-generation poll), so pull in a new server value only for
  // fields the admin hasn't edited: edits survive, and Save never writes a stale value back over
  // one that arrived meanwhile (such as a freshly generated description).
  const wasOpenRef = useRef(false);
  const seededRef = useRef<OrgContextValues>(values);
  useEffect(() => {
    const seeded = seededRef.current;
    if (isOpen && !wasOpenRef.current) {
      setDraft(values);
      seededRef.current = values;
    } else if (
      isOpen &&
      ORG_CONTEXT_FIELDS.some(({ key }) => values[key] !== seeded[key])
    ) {
      setDraft((prev) => {
        const next = { ...prev };
        for (const { key } of ORG_CONTEXT_FIELDS) {
          if (prev[key] === seeded[key]) {
            next[key] = values[key];
          }
        }
        return next;
      });
      seededRef.current = values;
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, values]);

  // Focus the requested field once the drawer content has mounted/animated in. Skipped when
  // there's no focus target (e.g. opened via the "Edit" button); the cleanup cancels the
  // pending focus if the drawer closes before the delay elapses.
  useEffect(() => {
    if (!isOpen || !focusField) return;
    const timeoutId = setTimeout(() => {
      focusOrgContextField(fieldRefs.current[focusField]);
    }, FOCUS_AFTER_OPEN_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [isOpen, focusField]);

  const handleCancel = () => {
    setDraft(values);
    onClose();
  };

  const handleConfirm = async () => {
    // Guard re-entry: the confirm button stays clickable during the async save, so without
    // this a double-click would fire duplicate writes. The parent persists each field to its
    // correct store (description → accounts, ICP free-text → account_settings); the drawer is
    // a pure editor. Only close once every write succeeds — if onSaved rejects, the throw
    // skips the close (drawer stays open, edit isn't lost) and Sentry's global handler reports
    // it; the finally clears the pending flag either way so the user can retry.
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onSaved(draft);
      setShowConfirm(false);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const fields = ORG_CONTEXT_FIELDS.map(({ key, namespace }) => ({
    key,
    title: t(`${TRANSLATION_KEY}.${namespace}.title`),
    subTitle: t(`${TRANSLATION_KEY}.${namespace}.subtitle`),
    placeholder: t(`${TRANSLATION_KEY}.${namespace}.placeholder`)
  }));

  return (
    <>
      <Drawer
        open={isOpen}
        direction="right"
        fixed
        modal={false}
        disablePreventScroll={false}
        noBodyStyles
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer initialWidth={480}>
          <DrawerHeader>
            <DrawerTitle>
              <Text variant="lgSubtitle">
                {t(`${TRANSLATION_KEY}.drawerTitle`)}
              </Text>
            </DrawerTitle>
            <IconButton
              data-test="close-org-context-drawer"
              onClick={handleCancel}
              variant="iconNoBg"
              size="sm"
            >
              <Close />
            </IconButton>
          </DrawerHeader>

          <div className="w-full flex-1 overflow-y-auto p-1">
            {fields.map((field, index) => {
              const item: SettingItemLayoutProps = {
                title: field.title,
                subTitle: field.subTitle,
                actionType: "textarea",
                isLastItem: index === fields.length - 1,
                actionProps: {
                  value: draft[field.key],
                  placeholder: field.placeholder,
                  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setDraft((prev) => ({
                      ...prev,
                      [field.key]: e.target.value
                    }))
                }
              };
              return (
                <div
                  key={field.key}
                  ref={(element) => {
                    fieldRefs.current[field.key] = element;
                  }}
                >
                  <SettingItemLayout {...item} />
                </div>
              );
            })}
          </div>

          <DrawerFooter>
            <div className="flex justify-end gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
              <Button
                data-test="org-context-cancel"
                variant="secondaryNoBg"
                onClick={handleCancel}
              >
                {t("cancel")}
              </Button>
              <Button
                data-test="org-context-save"
                onClick={() => setShowConfirm(true)}
              >
                {t("save")}
              </Button>
            </div>
          </DrawerFooter>
        </AppDrawerContent>
      </Drawer>

      <ConfirmationModal
        isOpen={showConfirm}
        setIsOpen={setShowConfirm}
        isPrimaryButton
        title={t(`${TRANSLATION_KEY}.confirm.title`)}
        confirmationText={t(`${TRANSLATION_KEY}.confirm.body`)}
        submitButtonText={t(`${TRANSLATION_KEY}.confirm.updateContext`)}
        cancelButtonText={t(`${TRANSLATION_KEY}.confirm.keepEditing`)}
        handleSubmit={handleConfirm}
        data-test="org-context-confirm-modal"
      />
    </>
  );
};

export default OrgContextDrawer;
